<script setup lang="ts">
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../../configs/text';
import { AttemptType, VocabResult, VocabResultDecision } from '../../../types/session';
import localforage from 'localforage';
import { onMounted, ref } from 'vue';
import AudioPlayer from '../../General/AudioPlayer.vue';

const { result } = defineProps<{ result: VocabResult }>()
const audioUrl = ref<string>('')

function getTextColor(decision: VocabResultDecision): string {
    switch (decision) {
        case VocabResultDecision.correct:
            return 'text-lime-500';
        case VocabResultDecision.warning:
            return 'text-yellow-500';
        case VocabResultDecision.incorrect:
            return 'text-orange-600';
        default:
            return '';
    }
}

onMounted(async () => {
    const audioBlob = await localforage.getItem<Blob>(result.key || '')
    audioUrl.value = audioBlob ? URL.createObjectURL(audioBlob) : ''
})

const { t } = useI18n()
</script>
<template>
    <div class="w-full text-red-500 text-sm text-center mb-3 p-3" v-if="result.attempt_type !== AttemptType.RELEVANT">
        {{ t(result.attempt_type === AttemptType.UNSPOKEN ? TEXT.no_sound_detected : TEXT.the_sound_not_match) }}
    </div>
    <div class="flex flex-wrap items-center justify-between gap-3 w-full mb-3 p-3">
        <div>
            <h3 class="w-full text-xl">
                {{ t(TEXT.pronunciation_result) }}:
                <span class="font-bold capitalize ml-3" :class="getTextColor(result.decision)">
                    {{ t(TEXT[VocabResultDecision[result.decision]]) }}
                </span>
            </h3>
            <h3 class="w-full text-xl">{{ t(TEXT.score) }}:
                <span class="font-bold ml-3" :class="getTextColor(result.decision)">
                    {{ result.pronunciation_score }}%
                </span>
            </h3>
        </div>
        <AudioPlayer v-if="audioUrl" :src="audioUrl" :label="t(TEXT.your_record)" />
    </div>
    <div class="p-3">
        <table class="table-auto w-full">
            <thead>
                <tr>
                    <th class="capitalize">{{ t(TEXT.result) }}</th>
                    <th class="capitalize">{{ t(TEXT.sound) }}</th>
                    <th class="capitalize">{{ t(TEXT.feedback) }}</th>
                </tr>
            </thead>
            <tbody v-for="word in result.words">
                <tr v-for="phoneme in word.phonemes">
                    <td class="font-bold" :class="getTextColor(phoneme.decision)">
                        {{ t(TEXT[VocabResultDecision[phoneme.decision]]) }}
                    </td>
                    <td class="font-bold">/{{ phoneme.ipa }}/</td>
                    <td>
                        <ul>
                            <li class="whitespace-pre-wrap" v-for="feedback in phoneme.feedback">
                                {{ feedback.text }}
                            </li>
                        </ul>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style lang="postcss" scoped>
thead {
    @apply bg-neutral-200/50
}

tbody {
    @apply bg-white
}

th,
td {
    @apply border-[1px] border-neutral-200 p-2
}
</style>